/**
 * 前端导出工具
 *
 * 财务用户习惯把列表导出到 Excel 二次核对，这里提供轻量 CSV 导出（带 BOM，
 * Excel 直接打开不乱码）。真正的审计导出走后端 /archives/export 并留痕，
 * 本工具只导出「当前页面所见的数据」，两者不要混淆。
 */

import { formatCents } from '@ilc/shared';

export interface ExportColumn<T> {
  title: string;
  /** 取值函数；不填时尝试用 dataIndex */
  value?: (row: T) => unknown;
  dataIndex?: string | string[];
}

function pick(row: unknown, dataIndex?: string | string[]): unknown {
  if (!dataIndex) return '';
  const keys = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
  let current: unknown = row;
  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') return '';
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

function cellToString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function escapeCsv(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

/** 导出为 CSV 文件（文件名自动带日期） */
export function exportCsv<T>(fileName: string, columns: ExportColumn<T>[], rows: T[]): void {
  const header = columns.map((c) => escapeCsv(c.title)).join(',');
  const body = rows
    .map((row) =>
      columns
        .map((col) => {
          const raw = col.value ? col.value(row) : pick(row, col.dataIndex);
          return escapeCsv(cellToString(raw));
        })
        .join(','),
    )
    .join('\r\n');

  const csv = `﻿${header}\r\n${body}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const today = new Date();
  const stamp = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(
    today.getDate(),
  ).padStart(2, '0')}`;

  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}_${stamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** 金额导出：分 → 元字符串，保持两位小数，便于 Excel 求和 */
export function exportMoney(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return formatCents(cents);
}
